import React, { useState, useEffect, useRef } from "react";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Box,
  TextField,
  Button,
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { toast } from "react-toastify";
import api from "../../services/api";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { useReactToPrint } from "react-to-print";
import PrintIcon from "@material-ui/icons/Print";
import GetAppIcon from "@material-ui/icons/GetApp";
import { exportToExcel, exportToExcelMultiSheet } from "../../utils/exportToExcel";
import UsageReportDetailsModal from "../../components/UsageReportDetailsModal";
import UsageReportGeneralPrintTemplate from "./components/UsageReportGeneralPrintTemplate";
import UsageReportDetailedPrintTemplate from "./components/UsageReportDetailedPrintTemplate";
import KanbanCardsReportPrintTemplate from "./components/KanbanCardsReportPrintTemplate";
import KanbanCardsReportView from "./components/KanbanCardsReportView";
import ContactsByUserChart from "./components/ContactsByUserChart";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  paper: {
    padding: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  title: {
    marginBottom: theme.spacing(3),
    fontWeight: "bold",
    color: theme.palette.primary.main,
  },
  tableContainer: {
    maxHeight: "calc(100vh - 300px)",
    marginTop: theme.spacing(2),
  },
  headerCell: {
    backgroundColor: theme.palette.type === "dark" ? "#37474f" : theme.palette.primary.main,
    color: "#ffffff",
    fontWeight: "bold",
    position: "sticky",
    top: 0,
    zIndex: 10,
    fontSize: "0.875rem",
    padding: theme.spacing(1),
    "&.MuiTableCell-stickyHeader": {
      backgroundColor: theme.palette.type === "dark" ? "#37474f" : theme.palette.primary.main,
      color: "#ffffff",
    },
  },
  cell: {
    fontSize: "0.875rem",
    padding: theme.spacing(1),
  },
  totalRow: {
    backgroundColor: theme.palette.action.hover,
    fontWeight: "bold",
  },
  filterContainer: {
    marginBottom: theme.spacing(2),
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    minHeight: 200,
  },
  periodInfo: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
  userName: {
    fontWeight: 500,
  },
  clickableNumber: {
    cursor: "pointer",
    color: theme.palette.primary.main,
    fontWeight: 600,
    textDecoration: "underline",
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    flexWrap: "wrap",
  },
  filterActionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center",
    height: "100%",
    "& > button": {
      flex: 1,
      minWidth: 0,
    },
  },
  viewTabs: {
    marginBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  chartContainer: {
    marginTop: theme.spacing(4),
    width: "100%",
  },
}));

const SystemUsageReport = () => {
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [users, setUsers] = useState([]);
  const [selectedUserProfile, setSelectedUserProfile] = useState("");
  const [selectedUserId, setSelectedUserId] = useState("");
  const [selectedReceiverUserId, setSelectedReceiverUserId] = useState("");
  const [selectedHistoryType, setSelectedHistoryType] = useState("");
  const [dateFrom, setDateFrom] = useState(() => {
    return format(new Date(), "yyyy-MM-dd");
  });
  const [dateTo, setDateTo] = useState(() => {
    return format(new Date(), "yyyy-MM-dd");
  });
  
  // Estados para o modal de detalhes
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalDetails, setModalDetails] = useState([]);
  const [modalUserName, setModalUserName] = useState("");
  const [modalDetailTypeName, setModalDetailTypeName] = useState("");
  const [modalPeriod, setModalPeriod] = useState("");
  const [detailedPrintData, setDetailedPrintData] = useState([]);
  const [loadingDetailedPrint, setLoadingDetailedPrint] = useState(false);

  const generalPrintRef = useRef();
  const detailedPrintRef = useRef();
  const kanbanCardsRef = useRef();

  const [kanbanCardsData, setKanbanCardsData] = useState(null);
  const [loadingKanbanReport, setLoadingKanbanReport] = useState(false);

  // Alternância de visualização: "general" | "forwarding"
  const [activeView, setActiveView] = useState("general");
  // Paginação client-side da visualização de encaminhamentos (por usuário destino)
  const [fwdPage, setFwdPage] = useState(0);
  const [fwdRowsPerPage, setFwdRowsPerPage] = useState(10);

  const parseDateStart = (value) => {
    if (!value) return null;
    const date = new Date(`${value}T00:00:00`);
    return Number.isNaN(date.getTime()) ? null : date;
  };

  const validateDateRange = (fromValue = dateFrom, toValue = dateTo) => {
    const from = parseDateStart(fromValue);
    const to = parseDateStart(toValue);

    if (!from || !to) {
      toast.error(i18n.t("usageReport.toasts.invalidDates"));
      return false;
    }

    if (from > to) {
      toast.error(i18n.t("usageReport.toasts.dateRange"));
      return false;
    }

    return true;
  };

  const normalizeHistoryType = (value) => {
    if (!value) return "";
    return String(value)
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toUpperCase()
      .replace(/[\s-]+/g, "_");
  };

  const historyTypes = (reportData?.contactMethods || []).map((label) => ({
    value: label,
    label,
  }));

  const getContactMethodCount = (user, methodLabel) => {
    if (!user?.contactMethods) return 0;
    if (Object.prototype.hasOwnProperty.call(user.contactMethods, methodLabel)) {
      return Number(user.contactMethods[methodLabel] || 0);
    }

    const target = normalizeHistoryType(methodLabel);
    const foundKey = Object.keys(user.contactMethods).find(
      (key) => normalizeHistoryType(key) === target
    );

    return foundKey ? Number(user.contactMethods[foundKey] || 0) : 0;
  };

  const visibleContactMethods = (reportData?.contactMethods || []).filter((methodLabel) => {
    if (!selectedHistoryType) return true;
    return normalizeHistoryType(methodLabel) === normalizeHistoryType(selectedHistoryType);
  });

  const availableProfiles = Array.from(
    new Set((users || []).map((user) => user.profile).filter(Boolean))
  ).sort((a, b) => a.localeCompare(b));

  const usersByProfile = selectedUserProfile
    ? users.filter((user) => user.profile === selectedUserProfile)
    : users;

  const formatProfileLabel = (profile) => {
    if (!profile) return "";
    if (profile.toLowerCase() === "admin") return i18n.t("usageReport.profiles.admin");
    return profile
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  // Função para abrir o modal com detalhes
  const handleOpenDetails = async (userId, userName, detailType, detailTypeName) => {
    setModalOpen(true);
    setModalLoading(true);
    setModalUserName(userName);
    setModalDetailTypeName(detailTypeName);
    setModalPeriod(formatPeriod());
    setModalDetails([]);

    try {
      const { data } = await api.get("/dashboard/usage-report-details", {
        params: {
          userId,
          dateFrom,
          dateTo,
          detailType
        }
      });
      setModalDetails(data);
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("usageReport.toasts.detailsError"));
    } finally {
      setModalLoading(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setModalDetails([]);
  };

  useEffect(() => {
    fetchUsers();
    fetchReport();
  }, []);

  useEffect(() => {
    if (!selectedUserId) return;

    const hasSelectedUserInProfile = usersByProfile.some(
      (user) => String(user.id) === String(selectedUserId)
    );

    if (!hasSelectedUserInProfile) {
      setSelectedUserId("");
    }
  }, [selectedUserProfile, users, selectedUserId]);

  useEffect(() => {
    if (!selectedReceiverUserId) return;

    const hasReceiverInProfile = usersByProfile.some(
      (user) => String(user.id) === String(selectedReceiverUserId)
    );

    if (!hasReceiverInProfile) {
      setSelectedReceiverUserId("");
    }
  }, [selectedUserProfile, users, selectedReceiverUserId]);

  const fetchUsers = async () => {
    try {
      const { data } = await api.get("/users/list");
      setUsers(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("usageReport.toasts.usersError"));
    }
  };

  // Aceita overrides explícitos para evitar usar valores de estado defasados
  // (ex.: ao limpar filtros, o estado ainda não foi atualizado no momento da busca).
  const fetchReport = async (overrides = {}) => {
    const f = {
      dateFrom,
      dateTo,
      userId: selectedUserId,
      profile: selectedUserProfile,
      historyType: selectedHistoryType,
      ...overrides,
    };

    if (!validateDateRange(f.dateFrom, f.dateTo)) return;

    setLoading(true);
    try {
      const params = {
        dateFrom: f.dateFrom,
        dateTo: f.dateTo,
      };

      if (f.userId) params.userId = f.userId;
      if (f.profile) params.profile = f.profile;
      if (f.historyType) params.historyType = f.historyType;

      const { data } = await api.get("/dashboard/usage-report", { params });
      setReportData(data);
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("usageReport.toasts.reportError"));
    } finally {
      setLoading(false);
    }
  };

  const fetchActiveView = (overrides) => {
    if (activeView === "forwarding") {
      fetchKanbanReport(overrides);
    } else {
      fetchReport(overrides);
    }
  };

  const handleFilter = () => {
    fetchActiveView();
  };

  const handleClearFilters = () => {
    const today = format(new Date(), "yyyy-MM-dd");
    setDateFrom(today);
    setDateTo(today);
    setSelectedUserId("");
    setSelectedReceiverUserId("");
    setSelectedUserProfile("");
    setSelectedHistoryType("");
    // Busca já com os valores limpos (não depende do estado ser atualizado antes).
    fetchActiveView({
      dateFrom: today,
      dateTo: today,
      userId: "",
      profile: "",
      historyType: "",
      senderId: "",
      receiverId: "",
    });
  };

  const handleChangeView = (event, newView) => {
    if (!newView || newView === activeView) return;
    setActiveView(newView);
    if (newView === "forwarding") {
      fetchKanbanReport();
    } else {
      fetchReport();
    }
  };

  const handleFwdChangePage = (event, newPage) => {
    setFwdPage(newPage);
  };

  const handleFwdChangeRowsPerPage = (event) => {
    setFwdRowsPerPage(parseInt(event.target.value, 10));
    setFwdPage(0);
  };

  const formatPeriod = () => {
    if (!reportData) return "";
    
    const fromDate = parseDateStart(reportData.period?.dateFrom);
    const toDate = parseDateStart(reportData.period?.dateTo);

    if (!fromDate || !toDate) return i18n.t("usageReport.invalidPeriod");
    
    return i18n.t("usageReport.periodRange", {
      from: format(fromDate, "dd/MM/yyyy", { locale: ptBR }),
      to: format(toDate, "dd/MM/yyyy", { locale: ptBR }),
    });
  };

  const formatPeriodFromFilters = () => {
    const fromDate = parseDateStart(dateFrom);
    const toDate = parseDateStart(dateTo);

    if (!fromDate || !toDate) return i18n.t("usageReport.invalidPeriod");

    return i18n.t("usageReport.periodRange", {
      from: format(fromDate, "dd/MM/yyyy", { locale: ptBR }),
      to: format(toDate, "dd/MM/yyyy", { locale: ptBR }),
    });
  };

  const formatPeriodKanban = () => {
    const fromDate = parseDateStart(kanbanCardsData?.period?.dateFrom);
    const toDate = parseDateStart(kanbanCardsData?.period?.dateTo);

    if (!fromDate || !toDate) return i18n.t("usageReport.invalidPeriod");

    return i18n.t("usageReport.periodRange", {
      from: format(fromDate, "dd/MM/yyyy", { locale: ptBR }),
      to: format(toDate, "dd/MM/yyyy", { locale: ptBR }),
    });
  };

  const handlePrintGeneral = useReactToPrint({
    contentRef: generalPrintRef,
    documentTitle: i18n.t("usageReport.files.generalPrint", { from: dateFrom, to: dateTo }),
    pageStyle: `
      @page {
        size: landscape;
        margin: 8mm;
      }
      @media print {
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
      }
    `
  });

  const handlePrintDetailed = useReactToPrint({
    contentRef: detailedPrintRef,
    documentTitle: i18n.t("usageReport.files.detailedPrint", { from: dateFrom, to: dateTo }),
    pageStyle: `
      @page {
        size: A4 landscape;
        margin: 6mm 4mm;
      }
      @media print {
        * {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        body {
          -webkit-print-color-adjust: exact;
          print-color-adjust: exact;
        }
        /* Permite que a tabela quebre entre páginas (em vez de ser empurrada
           inteira para a página seguinte deixando espaço em branco) */
        table {
          break-inside: auto !important;
          page-break-inside: auto !important;
        }
        thead {
          display: table-header-group;
        }
        /* Permite que uma linha muito alta comece na página atual e se divida
           para a próxima, em vez de ser empurrada inteira deixando espaço vazio */
        tr, td {
          break-inside: auto !important;
          page-break-inside: auto !important;
        }
        /* Mantém o nome do usuário/chips junto ao início da tabela */
        h2, h3 {
          break-after: avoid !important;
          page-break-after: avoid !important;
        }
      }
    `
  });

  const loadDetailedPrintData = async () => {
    if (!validateDateRange()) return;

    const params = {
      dateFrom,
      dateTo,
      ...(selectedUserId ? { userId: selectedUserId } : {}),
      ...(selectedUserProfile ? { profile: selectedUserProfile } : {}),
      ...(selectedHistoryType ? { historyType: selectedHistoryType } : {})
    };

    const { data } = await api.get("/dashboard/usage-report-details", { params });
    setDetailedPrintData(Array.isArray(data) ? data : []);
  };

  const handlePrintKanbanCards = useReactToPrint({
    contentRef: kanbanCardsRef,
    documentTitle: i18n.t("usageReport.files.forwardingPrint", { from: dateFrom, to: dateTo }),
    pageStyle: `@page { size: A4; margin: 8mm; }`
  });

  const fetchKanbanReport = async (overrides = {}) => {
    const f = {
      dateFrom,
      dateTo,
      senderId: selectedUserId,
      receiverId: selectedReceiverUserId,
      ...overrides,
    };

    if (!validateDateRange(f.dateFrom, f.dateTo)) return false;

    setLoadingKanbanReport(true);
    try {
      const params = {
        dateFrom: f.dateFrom,
        dateTo: f.dateTo,
        ...(f.senderId ? { senderId: f.senderId } : {}),
        ...(f.receiverId ? { receiverId: f.receiverId } : {}),
      };
      const { data } = await api.get("/kanban/cards/report", { params });
      setKanbanCardsData(data);
      setFwdPage(0);
      return true;
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("usageReport.toasts.forwardingError"));
      return false;
    } finally {
      setLoadingKanbanReport(false);
    }
  };

  const handleKanbanCardsClick = async () => {
    // Se os dados já estão carregados (aba aberta), imprime direto; senão, busca antes.
    if (kanbanCardsData) {
      handlePrintKanbanCards();
      return;
    }
    const ok = await fetchKanbanReport();
    if (!ok) return;
    setTimeout(() => {
      handlePrintKanbanCards();
    }, 120);
  };

  const handleDetailedPrintClick = async () => {
    setLoadingDetailedPrint(true);
    try {
      await loadDetailedPrintData();
      setTimeout(() => {
        handlePrintDetailed();
      }, 120);
    } catch (err) {
      console.error(err);
      toast.error(i18n.t("usageReport.toasts.detailedPrintError"));
    } finally {
      setLoadingDetailedPrint(false);
    }
  };

  const handleExportExcelGeral = () => {
    const methods = reportData?.contactMethods || [];
    const rows = (reportData?.users || []).map((user) => {
      const row = {
        [i18n.t("usageReport.columns.user")]: user.userName,
        [i18n.t("usageReport.columns.registrations")]: user.cadastros,
        [i18n.t("usageReport.columns.totalContacts")]: user.visitas_ligacoes,
        [i18n.t("usageReport.columns.orders")]: user.pedidos,
      };
      methods.forEach((m) => {
        row[m] = getContactMethodCount(user, m);
      });
      return row;
    });
    exportToExcel(
      rows,
      i18n.t("usageReport.files.generalExcel", { from: dateFrom, to: dateTo }),
      i18n.t("usageReport.files.generalSheet")
    );
  };

  const handleExportExcelEncaminhamento = () => {
    const typeLabel = (t) => {
      if (t === "move") return i18n.t("usageReport.forwarding.types.move");
      if (t === "copy") return i18n.t("usageReport.forwarding.types.copy");
      return i18n.t("usageReport.forwarding.types.create");
    };
    exportToExcelMultiSheet(
      [
        {
          name: i18n.t("usageReport.forwarding.createdTitle"),
          rows: (kanbanCardsData?.createdStats || []).map((r) => ({
            [i18n.t("usageReport.columns.user")]: r.senderName || r.userName || "—",
            [i18n.t("usageReport.forwarding.cardsCreated")]: r.cardsCreated ?? r.count ?? 0,
          })),
        },
        {
          name: i18n.t("usageReport.tabs.forwarding"),
          rows: (kanbanCardsData?.forwardedToOthers || []).map((r) => ({
            [i18n.t("usageReport.forwarding.origin")]: r.senderName,
            [i18n.t("usageReport.forwarding.destination")]: r.receiverName,
            [i18n.t("common.type")]: typeLabel(r.type),
            [i18n.t("usageReport.forwarding.quantity")]: r.count,
          })),
        },
      ],
      i18n.t("usageReport.files.forwardingExcel", { from: dateFrom, to: dateTo })
    );
  };

  // Função para verificar se o usuário tem algum dado relevante
  const shouldShowUser = (user) => {
    // Se há filtro de usuário específico, sempre mostra (já vem filtrado do backend)
    if (selectedUserId) return true;
    
    // Se não há filtro de tipo de histórico, mostra todos os usuários
    if (!selectedHistoryType) return true;
    
    return getContactMethodCount(user, selectedHistoryType) > 0;
  };

  // Componente para renderizar célula clicável
  const ClickableCell = ({ value, userId, userName, detailType, detailTypeName }) => {
    if (!value || value === 0) {
      return <span>{value}</span>;
    }

    return (
      <span
        className={classes.clickableNumber}
        onClick={() => handleOpenDetails(userId, userName, detailType, detailTypeName)}
        title={i18n.t("usageReport.clickForDetails", { type: detailTypeName })}
      >
        {value}
      </span>
    );
  };

  // Dados do gráfico de contatos por usuário (aba Geral).
  // Reaproveita os mesmos usuários/valores da tabela e respeita os filtros.
  const chartUsers = (reportData?.users || []).filter(shouldShowUser);
  const chartData = chartUsers.map((user) => ({
    name: user.userName,
    quantidade: selectedHistoryType
      ? getContactMethodCount(user, selectedHistoryType)
      : Number(user.visitas_ligacoes || 0),
  }));
  const chartTitle = selectedHistoryType
    ? i18n.t("usageReport.chart.contactsByUserType", { type: selectedHistoryType })
    : i18n.t("usageReport.chart.totalContactsByUser");

  return (
    <div className={classes.root}>
      <Paper className={classes.paper}>
        <Typography variant="h5" className={classes.title}>
          {i18n.t("usageReport.title")}
        </Typography>

        <Tabs
          value={activeView}
          onChange={handleChangeView}
          indicatorColor="primary"
          textColor="primary"
          className={classes.viewTabs}
        >
          <Tab label={i18n.t("usageReport.tabs.general")} value="general" />
          <Tab label={i18n.t("usageReport.tabs.forwarding")} value="forwarding" />
        </Tabs>

        <Grid container spacing={2} className={classes.filterContainer}>
          <Grid item xs={12} sm={2}>
            <TextField
              label={i18n.t("common.startDate")}
              type="date"
              fullWidth
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <TextField
              label={i18n.t("common.endDate")}
              type="date"
              fullWidth
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              variant="outlined"
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>{i18n.t("usageReport.filters.profile")}</InputLabel>
              <Select
                value={selectedUserProfile}
                onChange={(e) => setSelectedUserProfile(e.target.value)}
                label={i18n.t("usageReport.filters.profile")}
              >
                <MenuItem value="">
                  <em>{i18n.t("common.all")}</em>
                </MenuItem>
                {availableProfiles.map((profile) => (
                  <MenuItem key={profile} value={profile}>
                    {formatProfileLabel(profile)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormControl variant="outlined" size="small" fullWidth>
              <InputLabel>
                {activeView === "forwarding" ? i18n.t("usageReport.filters.senderUser") : i18n.t("usageReport.filters.user")}
              </InputLabel>
              <Select
                value={selectedUserId}
                onChange={(e) => setSelectedUserId(e.target.value)}
                label={activeView === "forwarding" ? i18n.t("usageReport.filters.senderUser") : i18n.t("usageReport.filters.user")}
              >
                <MenuItem value="">
                  <em>{i18n.t("common.all")}</em>
                </MenuItem>
                {usersByProfile.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          {activeView === "forwarding" ? (
            <Grid item xs={12} sm={2}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>{i18n.t("usageReport.filters.receiverUser")}</InputLabel>
                <Select
                  value={selectedReceiverUserId}
                  onChange={(e) => setSelectedReceiverUserId(e.target.value)}
                  label={i18n.t("usageReport.filters.receiverUser")}
                >
                  <MenuItem value="">
                    <em>{i18n.t("common.all")}</em>
                  </MenuItem>
                  {usersByProfile.map((user) => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          ) : (
            <Grid item xs={12} sm={2}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>{i18n.t("usageReport.filters.historyType")}</InputLabel>
                <Select
                  value={selectedHistoryType}
                  onChange={(e) => setSelectedHistoryType(e.target.value)}
                  label={i18n.t("usageReport.filters.historyType")}
                >
                  <MenuItem value="">
                    <em>{i18n.t("common.all")}</em>
                  </MenuItem>
                  {historyTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          )}
          <Grid item xs={12} sm={2}>
            <Box className={classes.filterActionButtons}>
              <Button
                variant="contained"
                color="primary"
                size="small"
                onClick={handleFilter}
                disabled={loading}
              >
                {i18n.t("common.filter")}
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleClearFilters}
                disabled={loading}
              >
                {i18n.t("common.clear")}
              </Button>
            </Box>
          </Grid>
          <Grid item xs={12}>
            <Box className={classes.actionButtons}>
              {activeView === "general" ? (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PrintIcon />}
                    onClick={handlePrintGeneral}
                    disabled={loading || !reportData}
                  >
                    {i18n.t("usageReport.buttons.printGeneral")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<GetAppIcon />}
                    onClick={handleExportExcelGeral}
                    disabled={loading || !reportData}
                  >
                    {i18n.t("common.exportExcel")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PrintIcon />}
                    onClick={handleDetailedPrintClick}
                    disabled={loading || !reportData || loadingDetailedPrint}
                  >
                    {i18n.t("usageReport.buttons.printDetailed")}
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<PrintIcon />}
                    onClick={handleKanbanCardsClick}
                    disabled={loadingKanbanReport || !kanbanCardsData}
                  >
                    {loadingKanbanReport ? i18n.t("common.loading") : i18n.t("usageReport.buttons.printForwarding")}
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<GetAppIcon />}
                    onClick={handleExportExcelEncaminhamento}
                    disabled={loadingKanbanReport || !kanbanCardsData}
                  >
                    {i18n.t("common.exportExcel")}
                  </Button>
                </>
              )}
            </Box>
          </Grid>
        </Grid>

        {activeView === "general" && reportData && (
          <Typography variant="subtitle2" className={classes.periodInfo}>
            {i18n.t("usageReport.periodLabel", { period: formatPeriod() })}
          </Typography>
        )}
        {activeView === "forwarding" && kanbanCardsData && (
          <Typography variant="subtitle2" className={classes.periodInfo}>
            {i18n.t("usageReport.periodLabel", { period: formatPeriodKanban() })}
          </Typography>
        )}

        {activeView === "general" ? (
          loading ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : reportData ? (
          <>
          <TableContainer className={classes.tableContainer}>
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>{i18n.t("usageReport.columns.user")}</TableCell>
                  <TableCell className={classes.headerCell} align="center">{i18n.t("usageReport.columns.registrations")}</TableCell>
                  <TableCell className={classes.headerCell} align="center">{i18n.t("usageReport.columns.totalContacts")}</TableCell>
                  <TableCell className={classes.headerCell} align="center">{i18n.t("usageReport.columns.orders")}</TableCell>
                  {visibleContactMethods.map((methodLabel) => (
                    <TableCell key={methodLabel} className={classes.headerCell} align="center">{methodLabel}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {reportData.users.filter(shouldShowUser).map((user) => (
                  <TableRow key={user.userId} hover>
                    <TableCell className={classes.cell}>
                      <span className={classes.userName}>{user.userName}</span>
                    </TableCell>
                    <TableCell className={classes.cell} align="center">
                      <ClickableCell
                        value={user.cadastros}
                        userId={user.userId}
                        userName={user.userName}
                        detailType="cadastros"
                        detailTypeName={i18n.t("usageReport.columns.registrations")}
                      />
                    </TableCell>
                    <TableCell className={classes.cell} align="center">
                      <ClickableCell
                        value={user.visitas_ligacoes}
                        userId={user.userId}
                        userName={user.userName}
                        detailType="total_contatos"
                        detailTypeName={i18n.t("usageReport.columns.totalContacts")}
                      />
                    </TableCell>
                    <TableCell className={classes.cell} align="center">{user.pedidos}</TableCell>
                    {visibleContactMethods.map((methodLabel) => (
                      <TableCell key={`${user.userId}-${methodLabel}`} className={classes.cell} align="center">
                        <ClickableCell 
                          value={getContactMethodCount(user, methodLabel)} 
                          userId={user.userId} 
                          userName={user.userName}
                          detailType={methodLabel}
                          detailTypeName={methodLabel}
                        />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
                
                {/* Linha de Totais */}
                <TableRow className={classes.totalRow}>
                  <TableCell className={classes.cell}>
                    <strong>{i18n.t("common.total")}:</strong>
                  </TableCell>
                  <TableCell className={classes.cell} align="center">
                    <strong>{reportData.totals.cadastros}</strong>
                  </TableCell>
                  <TableCell className={classes.cell} align="center">
                    <strong>{reportData.totals.visitas_ligacoes}</strong>
                  </TableCell>
                  <TableCell className={classes.cell} align="center">
                    <strong>{reportData.totals.pedidos}</strong>
                  </TableCell>
                  {visibleContactMethods.map((methodLabel) => (
                    <TableCell key={`total-${methodLabel}`} className={classes.cell} align="center">
                      <strong>{reportData.totals?.contactMethods?.[methodLabel] || 0}</strong>
                    </TableCell>
                  ))}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
          <Box
            className={classes.chartContainer}
            style={{ height: Math.max(280, chartUsers.length * 36) }}
          >
            <ContactsByUserChart data={chartData} title={chartTitle} />
          </Box>
          </>
        ) : (
          <Box className={classes.loadingContainer}>
            <Typography variant="body1" color="textSecondary">
              {i18n.t("usageReport.emptyGeneral")}
            </Typography>
          </Box>
        )
        ) : loadingKanbanReport ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : kanbanCardsData ? (
          <KanbanCardsReportView
            reportData={kanbanCardsData}
            page={fwdPage}
            rowsPerPage={fwdRowsPerPage}
            onChangePage={handleFwdChangePage}
            onChangeRowsPerPage={handleFwdChangeRowsPerPage}
          />
        ) : (
          <Box className={classes.loadingContainer}>
            <Typography variant="body1" color="textSecondary">
              {i18n.t("usageReport.emptyForwarding")}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Modal de Detalhes */}
      <UsageReportDetailsModal
        open={modalOpen}
        onClose={handleCloseModal}
        details={modalDetails}
        loading={modalLoading}
        userName={modalUserName}
        detailTypeName={modalDetailTypeName}
        period={modalPeriod}
      />

      {/* Templates de impressão (ocultos na tela) */}
      <div style={{ display: "none" }}>
        <UsageReportGeneralPrintTemplate ref={generalPrintRef} reportData={reportData} />
      </div>
      <div style={{ display: "none" }}>
        <UsageReportDetailedPrintTemplate
          ref={detailedPrintRef}
          details={detailedPrintData}
          periodLabel={formatPeriodFromFilters()}
        />
      </div>
      <div style={{ display: "none" }}>
        <KanbanCardsReportPrintTemplate ref={kanbanCardsRef} reportData={kanbanCardsData} />
      </div>
    </div>
  );
};

export default SystemUsageReport;
